// Clients list page — full CRUD via ClientFormDialog
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Building2, Globe2, Plus, MoreVertical, Pencil, ArrowRight, Activity } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import Pill from "@/components/Pill";
import { pillForRisk, pillForStatus } from "@/lib/constants";
import { TID } from "@/lib/testIds";
import ClientFormDialog from "@/components/ClientFormDialog";

const CAN_EDIT_ROLES = new Set(["director", "administrator"]);

export default function ClientsPage() {
    const [clients, setClients] = useState([]);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [dialog, setDialog] = useState({ open: false, mode: "create", initial: null });
    const [menuRowId, setMenuRowId] = useState(null);
    const [portalBusyId, setPortalBusyId] = useState(null);
    const [pulseTestBusyId, setPulseTestBusyId] = useState(null);
    const { enterPortal, clients: ctxClients } = useView();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);

    const refresh = useCallback(async () => {
        const { data } = await api.get("/clients");
        setClients(data);
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (searchParams.get("new") === "1" && canEdit) {
            setDialog({ open: true, mode: "create", initial: null });
            searchParams.delete("new"); setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, canEdit, setSearchParams]);

    useEffect(() => {
        const openId = searchParams.get("open");
        if (openId && clients.length) {
            const c = clients.find((r) => r.id === openId);
            if (c && canEdit) setDialog({ open: true, mode: "edit", initial: c });
            searchParams.delete("open"); setSearchParams(searchParams, { replace: true });
        }
    }, [clients, canEdit, searchParams, setSearchParams]);

    const filtered = clients.filter((c) => {
        if (statusFilter !== "All" && c.status !== statusFilter) return false;
        if (q && !`${c.name} ${c.sector} ${c.account_manager}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    function openCreate() { setDialog({ open: true, mode: "create", initial: null }); }
    function openEdit(c) { setDialog({ open: true, mode: "edit", initial: c }); setMenuRowId(null); }
    function closeDialog() { setDialog((d) => ({ ...d, open: false })); }

    async function togglePortal(c) {
        setPortalBusyId(c.id);
        const next = !c.portal_enabled;
        setClients((rows) => rows.map((r) => r.id === c.id ? { ...r, portal_enabled: next } : r));
        try {
            await api.patch(`/clients/${c.id}`, { portal_enabled: next });
            window.dispatchEvent(new Event("clients:changed"));
        } catch (e) {
            setClients((rows) => rows.map((r) => r.id === c.id ? { ...r, portal_enabled: !next } : r));
        } finally { setPortalBusyId(null); }
    }

    async function sendPulseTest(c) {
        setPulseTestBusyId(c.id);
        setMenuRowId(null);
        try {
            const { data } = await api.post(`/pulse/send-test/${c.id}`);
            toast.success(`Test Pulse digest for ${c.name} sent to ${user.email} (${data.recipient_count} email${data.recipient_count === 1 ? "" : "s"}).`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not send the test digest.");
        } finally { setPulseTestBusyId(null); }
    }

    async function afterSaved() {
        await refresh();
        // Also refresh the view context so the sidebar switcher shows new/renamed clients
        // (the ViewContext re-fetches on user change; here we trigger a soft reload)
        try {
            const { data } = await api.get("/clients");
            // forcibly mirror into ctxClients via a custom event would be heavier; the
            // sidebar fetches on mount and on user change. For now, encourage a refresh.
            if (data.length !== ctxClients.length) window.dispatchEvent(new Event("clients:changed"));
        } catch (e) { console.debug("clients soft-refresh skipped:", e?.message); }
    }

    return (
        <div data-testid={TID.clientsPage} className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                          style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        Client management
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">All clients</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {clients.length} active organisations. Click a row to switch into their branded portal.
                    </p>
                </div>
                {canEdit && (
                    <button
                        data-testid="clients-new-button"
                        onClick={openCreate}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white transition-colors"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#9F4212")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "var(--brand-accent, #C75A1B)")}
                    >
                        <Plus className="w-3.5 h-3.5" /> New client
                    </button>
                )}
            </div>

            <div className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                {/* Toolbar */}
                <div className="flex items-center gap-3 px-4 py-3 border-b flex-wrap" style={{ borderColor: "#F0EDE8" }}>
                    <div className="flex items-center gap-2 bg-[#F6F4F0] border rounded-[8px] px-3 py-1.5 flex-1 min-w-[220px] max-w-[340px]"
                         style={{ borderColor: "#E5E0DA" }}>
                        <Search className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by name, sector or manager…"
                            className="flex-1 bg-transparent outline-none text-[12.5px]"
                            data-testid="clients-search"
                        />
                    </div>
                    <div className="flex items-center gap-2 text-[12px]">
                        <span style={{ color: "#6F6A64" }}>Status</span>
                        <select value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="bg-white border rounded-[8px] px-2 py-1.5 font-semibold text-[#1F1F1F] outline-none"
                                style={{ borderColor: "#E5E0DA" }}
                                data-testid="clients-status-filter">
                            <option>All</option>
                            <option>Active</option>
                            <option>Onboarding</option>
                            <option>Paused</option>
                            <option>Archived</option>
                        </select>
                    </div>
                    <div className="ml-auto text-[11.5px]" style={{ color: "#6F6A64" }}>
                        {filtered.length} shown · {clients.length} total
                    </div>
                </div>

                <table className="w-full text-[12.7px]">
                    <thead>
                        <tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                            <th className="text-left px-5 py-2.5 font-bold">Client</th>
                            <th className="text-left px-3 py-2.5 font-bold">Sector</th>
                            <th className="text-left px-3 py-2.5 font-bold">Account manager</th>
                            <th className="text-left px-3 py-2.5 font-bold">Buildings</th>
                            <th className="text-left px-3 py-2.5 font-bold">Projects</th>
                            <th className="text-left px-3 py-2.5 font-bold">Portal</th>
                            <th className="text-left px-3 py-2.5 font-bold">BSC</th>
                            <th className="text-left px-3 py-2.5 font-bold">Risk</th>
                            <th className="text-left px-3 py-2.5 font-bold">Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((c) => (
                            <tr
                                key={c.id}
                                data-testid={TID.clientRow(c.slug)}
                                className="border-t hover:bg-[#FCFBF9] cursor-pointer transition-colors"
                                style={{ borderColor: "#F0EDE8" }}
                                onClick={(e) => {
                                    // Avoid row-click hijacking the kebab menu / action buttons
                                    if (e.target.closest("[data-row-action]")) return;
                                    enterPortal(c.id);
                                }}
                            >
                                <td className="px-5 py-3">
                                    <div className="flex items-center gap-2.5">
                                        <span className="w-8 h-8 rounded-[7px] grid place-items-center text-[11px] font-bold text-white"
                                              style={{ background: c.branding.primary_colour }}>
                                            {c.branding.logo_initials}
                                        </span>
                                        <div>
                                            <div className="font-semibold text-[#1F1F1F]">{c.name}</div>
                                            <div className="text-[10.5px]" style={{ color: "#6F6A64" }}>{c.trading_name}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-3 py-3 text-[#2D2D2D]">{c.sector}</td>
                                <td className="px-3 py-3 text-[#2D2D2D]">{c.account_manager}</td>
                                <td className="px-3 py-3 text-[#2D2D2D]">
                                    <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3 text-[#9A968F]" />{c.buildings_count}</span>
                                </td>
                                <td className="px-3 py-3 text-[#2D2D2D]">{c.projects_count}</td>
                                <td className="px-3 py-3" data-row-action>
                                    {canEdit ? (
                                        <button
                                            type="button"
                                            data-testid={`client-portal-toggle-${c.slug}`}
                                            onClick={() => togglePortal(c)}
                                            disabled={portalBusyId === c.id}
                                            title={c.portal_enabled ? "Disable client portal" : "Enable client portal"}
                                            className="inline-flex items-center gap-2 disabled:opacity-60"
                                        >
                                            <span className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${c.portal_enabled ? "bg-emerald-500" : "bg-[#D9D4CC]"}`}>
                                                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${c.portal_enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                                            </span>
                                            <span className="text-[11px] font-semibold" style={{ color: c.portal_enabled ? "#1E7A46" : "#6F6A64" }}>
                                                {c.portal_enabled ? "Enabled" : "Disabled"}
                                            </span>
                                        </button>
                                    ) : (
                                        <Pill tone={c.portal_enabled ? "green" : "neutral"} icon={Globe2}>
                                            {c.portal_enabled ? "Enabled" : "Disabled"}
                                        </Pill>
                                    )}
                                </td>
                                <td className="px-3 py-3">
                                    <Pill tone={c.bsc_enabled ? "blue" : "neutral"}>
                                        {c.bsc_enabled ? "Enabled" : "—"}
                                    </Pill>
                                </td>
                                <td className="px-3 py-3"><Pill tone={pillForRisk(c.risk)}>{c.risk}</Pill></td>
                                <td className="px-3 py-3"><Pill tone={pillForStatus(c.status)}>{c.status}</Pill></td>
                                <td className="px-3 py-3 text-right relative" data-row-action>
                                    {canEdit ? (
                                        <>
                                            <button
                                                data-testid={`client-row-menu-${c.slug}`}
                                                onClick={() => setMenuRowId(menuRowId === c.id ? null : c.id)}
                                                className="w-8 h-8 rounded-[7px] grid place-items-center hover:bg-[#F6F4F0]"
                                                aria-label="Row actions"
                                            >
                                                <MoreVertical className="w-4 h-4" style={{ color: "#6F6A64" }} />
                                            </button>
                                            {menuRowId === c.id && (
                                                <div className="absolute right-3 top-[80%] bg-white border rounded-lg shadow-brand min-w-[180px] py-1 z-30"
                                                     style={{ borderColor: "#E5E0DA" }}>
                                                    <button
                                                        data-testid={`client-row-edit-${c.slug}`}
                                                        onClick={() => openEdit(c)}
                                                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] hover:bg-[#F6F4F0]"
                                                    >
                                                        <Pencil className="w-3.5 h-3.5" /> Edit details
                                                    </button>
                                                    <button
                                                        onClick={() => { enterPortal(c.id); setMenuRowId(null); }}
                                                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] hover:bg-[#F6F4F0]"
                                                    >
                                                        <ArrowRight className="w-3.5 h-3.5" /> Open portal
                                                    </button>
                                                    <button
                                                        data-testid={`client-row-send-pulse-test-${c.slug}`}
                                                        onClick={() => sendPulseTest(c)}
                                                        disabled={pulseTestBusyId === c.id}
                                                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] hover:bg-[#F6F4F0] disabled:opacity-50"
                                                    >
                                                        <Activity className="w-3.5 h-3.5" /> {pulseTestBusyId === c.id ? "Sending…" : "Send test Pulse to me"}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <span className="text-[var(--brand-accent,_#C75A1B)] text-[11px] font-semibold">Open portal →</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan={10} className="px-5 py-10 text-center italic" style={{ color: "#9A968F" }}>
                                No clients match the filters.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <ClientFormDialog
                open={dialog.open}
                mode={dialog.mode}
                initial={dialog.initial}
                onClose={closeDialog}
                onSaved={afterSaved}
            />
        </div>
    );
}
